import React from 'react';
import { useHistory } from 'react-router-dom';
import { useObserver } from 'mobx-react-lite';
import useLocalStorage from 'react-use-localstorage';

import { useStores } from 'src/hooks/use-stores';
import { Login } from 'src/components/user-flow/login';
import { isAuthorizedRedirectPath } from './is-authorized-redirect-path';

function requestedRedirectPath(storedPath: string | null | undefined): string {
  return storedPath?.length ? storedPath : '/redirect';
}

export default function LoginRoute(): JSX.Element {
  const history = useHistory();
  const { User } = useStores();
  const loggedIn = useObserver(() => User.loggedIn);
  const [redirectAfterLoginPath, setRedirectAfterLogin] = useLocalStorage('redirectAfterLogin', '');

  React.useEffect(() => {
    if (loggedIn) {
      history.replace('/redirect');
    }
  }, [history, loggedIn]);

  function handleOpenForgotPassword(): void {
    history.push('/forgot-password');
  }

  function onLoginSuccess(): void {
    const requestedPath = requestedRedirectPath(redirectAfterLoginPath);
    // `redirectAfterLoginPath` is a single global (not per-user) localStorage key written by
    // PrivateRoute before authentication — on a shared/kiosk machine, or simply if it's stale,
    // it can point at a chain/dispensary/enterprise the user who just logged in doesn't own.
    // Fall back to the default landing route rather than blindly trusting it.
    const path = isAuthorizedRedirectPath(requestedPath, User) ? requestedPath : '/redirect';
    try {
      history.replace(path);
    } catch (e) {
      console.error(e);
      history.replace('/redirect');
    } finally {
      setRedirectAfterLogin('');
    }
  }

  // Preserve the legacy per-render callback identities during the type-only conversion.
  // eslint-disable-next-line react/jsx-no-bind
  return <Login onOpenForgotPassword={handleOpenForgotPassword} onSuccess={onLoginSuccess} />;
}
